/**
 * Prescription order types for Medpharma MCare.
 * Aligned with API response from /prescriptions/query.
 */

export interface PrescriptionOrder {
  id: string | number;
  orderId: string;
  schedule_date: string;
  status: PrescriptionStatus;
  image: string | null;
  [key: string]: unknown;
}

export type PrescriptionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "cancelled"
  | "delivered"
  | string;

export interface PrescriptionListResponse {
  prescriptions: PrescriptionOrder[];
  totalPages: number;
}

/** Single prescription order detail from GET /prescriptions/:id */
export interface PrescriptionOrderDetail {
  id: number;
  orderId: number;
  status: PrescriptionStatus;
  image: string | null;
  timestamp?: string;
  createdAt?: string;
  updatedAt?: string;
  email?: string | null;
  name?: string | null;
  address?: string | null;
  contact?: string | null;
  landmark?: string | null;
  notes?: string | null;
  refill?: number | null;
  orderType?: string | null;
  paymentStatus?: string | null;
  payableAmount?: number | null;
  totalAmount?: number | null;
  deliveryCost?: number | null;
  tax?: number | null;
  delivery?: string | null;
  cancelReason?: string | null;
  insurance?: string | null;
  policyNumber?: string | null;
  deliveryMode?: { id?: number; title?: string; description?: string; key?: string; cost?: string } | null;
  newInsurance?: { id?: number; policyNumber?: string; provider?: string; providerName?: string } | null;
  newAddress?: { town?: string; landmark?: string; label?: string; type?: string; instructions?: string | null } | null;
  orderdetails?: PrescriptionOrderDetailItem[];
  user?: { fullname?: string; email?: string; phoneNumber?: string } | null;
}

/** Single line item (medication) in prescription order details */
export interface PrescriptionOrderDetailItem {
  id: number;
  drugName: string;
  quantity: number;
  unitPrice: number;
  duration?: string | null;
  frequency?: string | null;
  route?: string | null;
  dosage?: string | null;
  instructions?: string | null;
  notes?: string | null;
  timestamp?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const PRESCRIPTION_STATUS_CONFIG: Record<
  string,
  { label: string; colorClass: string; bgClass: string }
> = {
  pending: {
    label: "Pending",
    colorClass: "text-amber-700",
    bgClass: "bg-amber-50 border border-amber-200",
  },
  processing: {
    label: "Processing",
    colorClass: "text-blue-700",
    bgClass: "bg-blue-50 border border-blue-200",
  },
  completed: {
    label: "Completed",
    colorClass: "text-emerald-700",
    bgClass: "bg-emerald-50 border border-emerald-200",
  },
  delivered: {
    label: "Delivered",
    colorClass: "text-emerald-800",
    bgClass: "bg-emerald-50 border border-emerald-200",
  },
  cancelled: {
    label: "Cancelled",
    colorClass: "text-neutral-600",
    bgClass: "bg-neutral-100 border border-neutral-200",
  },
};

export function getStatusConfig(
  status: PrescriptionStatus
): { label: string; colorClass: string; bgClass: string } {
  const normalized = (status || "").toLowerCase().replace(/\s+/g, "_");
  return (
    PRESCRIPTION_STATUS_CONFIG[normalized] ?? {
      label: String(status || "—"),
      colorClass: "text-neutral-700",
      bgClass: "bg-neutral-50 border border-neutral-200",
    }
  );
}
